// License Admin Component - Enterprise License Management Dashboard
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Key, Building2, Clock, DollarSign, Users, Package,
  Plus, RefreshCw, ChevronDown, ChevronUp, AlertTriangle,
  CheckCircle, XCircle, Calendar, ArrowUpRight, Activity,
  Eye, Edit, MoreVertical, Search, Filter, Download,
  Gift, BarChart3, TrendingUp, Timer, Send, FileText,
  Mail, Copy, ExternalLink, X, Trash2
} from 'lucide-react';
import {
  getLicenseAdminStats,
  listLicenses,
  getLicense,
  createLicense,
  renewLicense,
  updateLicenseStatus,
  upgradeLicense,
  listTransactions,
  listTrialPackages,
  createTrialPackage,
  updateTrialPackage,
  deleteTrialPackage,
  getLicenseLogs,
  generateDeliveryPackage,
  getDeliveryPreview,
  sendDeliveryEmail,
  generateTrialPackage,
  getTrialPackagePreview,
  sendTrialPackageEmail,
  LICENSE_TIERS,
  LICENSE_STATUS,
  TRANSACTION_TYPES
} from '../services/license-admin-api';

// Format currency
const formatCurrency = (cents) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(cents / 100);
};

// Format date
const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Format relative time
const formatRelativeTime = (date) => {
  if (!date) return 'N/A';
  const d = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const diff = d - now;
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

  if (days < 0) return `${Math.abs(days)} days ago`;
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
};

// Status Badge Component
function StatusBadge({ status }) {
  const config = LICENSE_STATUS[status] || { label: status, color: 'gray' };
  const colors = {
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-700',
    orange: 'bg-orange-100 text-orange-700'
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[config.color] || colors.gray}`}>
      {config.label}
    </span>
  );
}

// Tier Badge Component
function TierBadge({ tier }) {
  const config = LICENSE_TIERS[tier] || { name: tier, color: 'gray' };
  const colors = {
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
    gray: 'bg-gray-100 text-gray-700 border-gray-200'
  };

  return (
    <span className={`px-2 py-1 rounded-md text-xs font-medium border ${colors[config.color] || colors.gray}`}>
      {config.name}
    </span>
  );
}

export default function LicenseAdmin() {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [stats, setStats] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [trialPackages, setTrialPackages] = useState([]);
  const [logs, setLogs] = useState([]);

  // UI states
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [showCreateLicense, setShowCreateLicense] = useState(false);
  const [showCreateTrial, setShowCreateTrial] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedLicense, setExpandedLicense] = useState(null);

  // Form states
  const [newLicense, setNewLicense] = useState({
    organization: '',
    tier: 'starter',
    durationDays: 365,
    contactEmail: '',
    contactName: '',
    notes: ''
  });

  const [newTrial, setNewTrial] = useState({
    name: '',
    description: '',
    tier: 'starter',
    durationDays: 14,
    maxRedemptions: 10
  });

  // Delivery modal states
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryLicense, setDeliveryLicense] = useState(null);
  const [deliveryOptions, setDeliveryOptions] = useState({
    includeClient: true,
    includeMonitoring: false
  });
  const [deliveryPreview, setDeliveryPreview] = useState(null);
  const [deliveryPreviewType, setDeliveryPreviewType] = useState('readme');
  const [deliveryEmail, setDeliveryEmail] = useState('');
  const [deliveryMessage, setDeliveryMessage] = useState('');
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [generatedPackage, setGeneratedPackage] = useState(null);

  // Trial package delivery modal states
  const [showTrialDeliveryModal, setShowTrialDeliveryModal] = useState(false);
  const [deliveryTrialPackage, setDeliveryTrialPackage] = useState(null);
  const [trialDeliveryEmail, setTrialDeliveryEmail] = useState('');
  const [trialDeliveryOrg, setTrialDeliveryOrg] = useState('');
  const [trialDeliveryMessage, setTrialDeliveryMessage] = useState('');
  const [trialDeliveryPreview, setTrialDeliveryPreview] = useState(null);
  const [trialDeliveryLoading, setTrialDeliveryLoading] = useState(false);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, licensesData, transData, trialsData] = await Promise.all([
        getLicenseAdminStats(),
        listLicenses(),
        listTransactions({ limit: 20 }),
        listTrialPackages()
      ]);

      setStats(statsData.stats);
      setLicenses(licensesData.licenses || []);
      setTransactions(transData.transactions || []);
      setTrialPackages(trialsData.packages || []);
    } catch (err) {
      console.error('Failed to load license data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter licenses
  const filteredLicenses = licenses.filter(l => {
    const matchesSearch = !searchQuery ||
      l.organization?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.licenseId?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || l.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Create new license
  const handleCreateLicense = async () => {
    if (!newLicense.organization) {
      setError('Organization name is required');
      return;
    }

    try {
      setLoading(true);
      const result = await createLicense(newLicense);
      if (result.success) {
        setLicenses([result.license, ...licenses]);
        setShowCreateLicense(false);
        setNewLicense({
          organization: '',
          tier: 'starter',
          durationDays: 365,
          contactEmail: '',
          contactName: '',
          notes: ''
        });
      }
    } catch (err) {
      setError('Failed to create license: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Renew license
  const handleRenewLicense = async (licenseId) => {
    if (!confirm('Renew this license for another year?')) return;

    try {
      const result = await renewLicense(licenseId, { durationDays: 365 });
      if (result.success) {
        loadData();
      }
    } catch (err) {
      setError('Failed to renew license: ' + err.message);
    }
  };

  // Suspend/Activate license
  const handleToggleStatus = async (license) => {
    const newStatus = license.status === 'active' ? 'suspended' : 'active';
    const reason = newStatus === 'suspended'
      ? prompt('Reason for suspension:')
      : 'Reactivated by admin';

    if (newStatus === 'suspended' && !reason) return;

    try {
      await updateLicenseStatus(license.id, newStatus, reason);
      loadData();
    } catch (err) {
      setError('Failed to update status: ' + err.message);
    }
  };

  // Create trial package
  const handleCreateTrial = async () => {
    if (!newTrial.name) {
      setError('Package name is required');
      return;
    }

    try {
      setLoading(true);
      const result = await createTrialPackage(newTrial);
      if (result.success) {
        setTrialPackages([result.package, ...trialPackages]);
        setShowCreateTrial(false);
        setNewTrial({
          name: '',
          description: '',
          tier: 'starter',
          durationDays: 14,
          maxRedemptions: 10
        });
      }
    } catch (err) {
      setError('Failed to create trial package: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Open delivery modal
  const handleOpenDelivery = async (license) => {
    setDeliveryLicense(license);
    setDeliveryEmail(license.contactEmail || '');
    setDeliveryMessage('');
    setGeneratedPackage(null);
    setDeliveryPreview(null);
    setShowDeliveryModal(true);

    // Load preview
    try {
      setDeliveryLoading(true);
      const result = await getDeliveryPreview(license.id, 'readme');
      setDeliveryPreview(result.preview?.content || '');
    } catch (err) {
      console.error('Failed to load preview:', err);
    } finally {
      setDeliveryLoading(false);
    }
  };

  // Load file preview
  const handleLoadPreview = async (fileType) => {
    if (!deliveryLicense) return;
    setDeliveryPreviewType(fileType);
    try {
      setDeliveryLoading(true);
      const result = await getDeliveryPreview(deliveryLicense.id, fileType);
      setDeliveryPreview(result.preview?.content || '');
    } catch (err) {
      setError('Failed to load preview: ' + err.message);
    } finally {
      setDeliveryLoading(false);
    }
  };

  // Generate and download package
  const handleGeneratePackage = async () => {
    if (!deliveryLicense) return;

    try {
      setDeliveryLoading(true);
      const result = await generateDeliveryPackage(deliveryLicense.id, deliveryOptions);
      if (result.success) {
        setGeneratedPackage(result.package);

        // Create and download ZIP file
        const { default: JSZip } = await import('jszip');
        const zip = new JSZip();

        // Add all files to ZIP
        Object.entries(result.package.files).forEach(([path, content]) => {
          zip.file(path, content);
        });

        // Add manifest
        zip.file('manifest.json', JSON.stringify(result.package.manifest, null, 2));

        // Generate and download
        const blob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.package.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError('Failed to generate package: ' + err.message);
    } finally {
      setDeliveryLoading(false);
    }
  };

  // Send delivery email
  const handleSendDeliveryEmail = async () => {
    if (!deliveryLicense || !deliveryEmail) {
      setError('Email address is required');
      return;
    }

    try {
      setDeliveryLoading(true);
      const result = await sendDeliveryEmail(
        deliveryLicense.id,
        deliveryEmail,
        deliveryMessage
      );
      if (result.success) {
        alert('Delivery email queued successfully!');
        setShowDeliveryModal(false);
      }
    } catch (err) {
      setError('Failed to send email: ' + err.message);
    } finally {
      setDeliveryLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  // Open trial delivery modal
  const handleOpenTrialDelivery = async (trialPkg) => {
    setDeliveryTrialPackage(trialPkg);
    setTrialDeliveryEmail('');
    setTrialDeliveryOrg('');
    setTrialDeliveryMessage('');
    setTrialDeliveryPreview(null);
    setShowTrialDeliveryModal(true);

    // Load preview
    try {
      setTrialDeliveryLoading(true);
      const result = await getTrialPackagePreview(trialPkg.id, 'readme');
      setTrialDeliveryPreview(result.preview?.content || '');
    } catch (err) {
      console.error('Failed to load trial preview:', err);
    } finally {
      setTrialDeliveryLoading(false);
    }
  };

  // Generate and download trial package
  const handleGenerateTrialPackage = async () => {
    if (!deliveryTrialPackage || !trialDeliveryOrg || !trialDeliveryEmail) {
      setError('Organization and email are required');
      return;
    }

    try {
      setTrialDeliveryLoading(true);
      const result = await generateTrialPackage(
        deliveryTrialPackage.id,
        trialDeliveryOrg,
        trialDeliveryEmail,
        { includeClient: true }
      );
      if (result.success) {
        // Create and download ZIP file
        const { default: JSZip } = await import('jszip');
        const zip = new JSZip();

        // Add all files to ZIP
        Object.entries(result.package.files).forEach(([path, content]) => {
          zip.file(path, content);
        });

        // Add manifest
        zip.file('manifest.json', JSON.stringify(result.package.manifest, null, 2));

        // Generate and download
        const blob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.package.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError('Failed to generate trial package: ' + err.message);
    } finally {
      setTrialDeliveryLoading(false);
    }
  };

  // Send trial package email
  const handleSendTrialEmail = async () => {
    if (!deliveryTrialPackage || !trialDeliveryEmail || !trialDeliveryOrg) {
      setError('Organization and email are required');
      return;
    }

    try {
      setTrialDeliveryLoading(true);
      const result = await sendTrialPackageEmail(
        deliveryTrialPackage.id,
        trialDeliveryEmail,
        trialDeliveryOrg,
        trialDeliveryMessage
      );
      if (result.success) {
        alert('Trial package email queued successfully!');
        setShowTrialDeliveryModal(false);
      }
    } catch (err) {
      setError('Failed to send trial email: ' + err.message);
    } finally {
      setTrialDeliveryLoading(false);
    }
  };

  // Delete trial package
  const handleDeleteTrialPackage = async (pkg) => {
    // Check if package has redemptions
    if (pkg.redemptionCount > 0) {
      setError(`Cannot delete "${pkg.name}" - it has been redeemed ${pkg.redemptionCount} time(s). Deactivate it instead.`);
      return;
    }

    if (!confirm(`Are you sure you want to delete the trial package "${pkg.name}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      const result = await deleteTrialPackage(pkg.id);
      if (result.success) {
        // Remove from local state
        setTrialPackages(trialPackages.filter(p => p.id !== pkg.id));
        alert(result.message || 'Trial package deleted successfully');
      }
    } catch (err) {
      setError('Failed to delete trial package: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'licenses', label: 'Licenses', icon: Key },
          { id: 'transactions', label: 'Transactions', icon: DollarSign },
          { id: 'trials', label: 'Trial Packages', icon: Gift }
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`px-4 py-2 rounded-t-lg font-medium text-sm flex items-center gap-2 transition-colors ${
                activeSubTab === tab.id
                  ? 'bg-white border border-b-0 border-gray-200 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}

        <button
          onClick={loadData}
          disabled={loading}
          className="ml-auto p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Overview Tab */}
      {activeSubTab === 'overview' && stats && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-6"
        >
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Key className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.licenses?.total || 0}</p>
                  <p className="text-sm text-gray-500">Total Licenses</p>
                </div>
              </div>
              <div className="mt-4 flex gap-2 text-xs">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">
                  {stats.licenses?.byStatus?.active || 0} active
                </span>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                  {stats.licenses?.byStatus?.trial || 0} trial
                </span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency((stats.revenue?.thisMonth || 0) * 100)}
                  </p>
                  <p className="text-sm text-gray-500">This Month</p>
                </div>
              </div>
              {stats.revenue?.growth && (
                <div className="mt-4 flex items-center gap-1 text-sm text-green-600">
                  <TrendingUp className="w-4 h-4" />
                  +{stats.revenue.growth}% vs last month
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Timer className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.licenses?.expiringIn30Days || 0}
                  </p>
                  <p className="text-sm text-gray-500">Expiring Soon</p>
                </div>
              </div>
              <p className="mt-4 text-xs text-amber-600">
                Needs renewal within 30 days
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Gift className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.trials?.totalRedemptions || 0}
                  </p>
                  <p className="text-sm text-gray-500">Trial Redemptions</p>
                </div>
              </div>
              <p className="mt-4 text-xs text-purple-600">
                {stats.trials?.activePackages || 0} active packages
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Recent Transactions
              </h3>
              {transactions.slice(0, 5).map(t => (
                <div key={t.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {t.license?.organization || 'Unknown'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {TRANSACTION_TYPES[t.transactionType]?.label || t.transactionType}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(t.amountCents)}
                    </p>
                    <p className="text-xs text-gray-400">{formatDate(t.createdAt)}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Licenses Needing Attention
              </h3>
              {licenses.filter(l => l.needsRenewal || l.isExpired).slice(0, 5).map(l => (
                <div key={l.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{l.organization}</p>
                    <p className="text-xs text-gray-500">{l.tier}</p>
                  </div>
                  <div className="text-right">
                    {l.isExpired ? (
                      <span className="text-xs text-red-600 font-medium">Expired</span>
                    ) : (
                      <span className="text-xs text-amber-600 font-medium">
                        {l.daysUntilExpiration} days left
                      </span>
                    )}
                    <button
                      onClick={() => handleRenewLicense(l.id)}
                      className="block mt-1 text-xs text-blue-600 hover:text-blue-700"
                    >
                      Renew
                    </button>
                  </div>
                </div>
              ))}
              {licenses.filter(l => l.needsRenewal || l.isExpired).length === 0 && (
                <p className="text-sm text-gray-400 py-4 text-center">All licenses are up to date</p>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Licenses Tab */}
      {activeSubTab === 'licenses' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          {/* Toolbar */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search organizations or license IDs..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="all">All Status</option>
              {Object.entries(LICENSE_STATUS).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
            <button
              onClick={() => setShowCreateLicense(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New License
            </button>
          </div>

          {/* Create License Modal */}
          {showCreateLicense && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Create New License
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization *</label>
                  <input
                    type="text"
                    value={newLicense.organization}
                    onChange={e => setNewLicense({ ...newLicense, organization: e.target.value })}
                    placeholder="Acme Corporation"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
                  <select
                    value={newLicense.tier}
                    onChange={e => setNewLicense({ ...newLicense, tier: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {Object.entries(LICENSE_TIERS).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.name} - ${config.price}/month
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (days)</label>
                  <input
                    type="number"
                    value={newLicense.durationDays}
                    onChange={e => setNewLicense({ ...newLicense, durationDays: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={newLicense.contactEmail}
                    onChange={e => setNewLicense({ ...newLicense, contactEmail: e.target.value })}
                    placeholder="admin@acme.com"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={newLicense.notes}
                    onChange={e => setNewLicense({ ...newLicense, notes: e.target.value })}
                    placeholder="Internal notes about this license..."
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleCreateLicense}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Create License
                </button>
                <button
                  onClick={() => setShowCreateLicense(false)}
                  className="px-6 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Licenses List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredLicenses.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      No licenses found
                    </td>
                  </tr>
                ) : (
                  filteredLicenses.map(license => (
                    <tr key={license.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{license.organization}</p>
                          <p className="text-xs text-gray-400">{license.licenseId}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <TierBadge tier={license.tier} />
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={license.status} />
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm text-gray-900">{formatDate(license.expiresAt)}</p>
                          {license.daysUntilExpiration !== null && (
                            <p className={`text-xs ${license.daysUntilExpiration <= 30 ? 'text-amber-600' : 'text-gray-400'}`}>
                              {formatRelativeTime(license.expiresAt)}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(license.priceCents)}
                          <span className="text-gray-400 font-normal">/mo</span>
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleOpenDelivery(license)}
                            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                            title="Send Installation Package"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRenewLicense(license.id)}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                            title="Renew"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(license)}
                            className={`p-1.5 rounded ${
                              license.status === 'active'
                                ? 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                                : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                            }`}
                            title={license.status === 'active' ? 'Suspend' : 'Activate'}
                          >
                            {license.status === 'active' ? (
                              <XCircle className="w-4 h-4" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => setExpandedLicense(expandedLicense === license.id ? null : license.id)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Transactions Tab */}
      {activeSubTab === 'transactions' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Transaction History</h3>
              <div className="text-sm text-gray-500">
                Total Revenue: <span className="font-semibold text-gray-900">
                  {formatCurrency((stats?.revenue?.total || 0) * 100)}
                </span>
              </div>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {transactions.map(t => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(t.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{t.license?.organization || 'Unknown'}</p>
                      <p className="text-xs text-gray-400">{t.license?.tier}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        t.transactionType === 'refund' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {TRANSACTION_TYPES[t.transactionType]?.label || t.transactionType}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className={`font-semibold ${t.transactionType === 'refund' ? 'text-red-600' : 'text-gray-900'}`}>
                        {t.transactionType === 'refund' ? '-' : ''}{formatCurrency(t.amountCents)}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        t.status === 'completed' ? 'bg-green-100 text-green-700' :
                        t.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {t.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Trial Packages Tab */}
      {activeSubTab === 'trials' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Trial Packages</h3>
            <button
              onClick={() => setShowCreateTrial(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Package
            </button>
          </div>

          {/* Create Trial Modal */}
          {showCreateTrial && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Create Trial Package</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Package Name *</label>
                  <input
                    type="text"
                    value={newTrial.name}
                    onChange={e => setNewTrial({ ...newTrial, name: e.target.value })}
                    placeholder="Enterprise Pilot Program"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
                  <select
                    value={newTrial.tier}
                    onChange={e => setNewTrial({ ...newTrial, tier: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white"
                  >
                    {Object.entries(LICENSE_TIERS).map(([key, config]) => (
                      <option key={key} value={key}>{config.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (days)</label>
                  <input
                    type="number"
                    value={newTrial.durationDays}
                    onChange={e => setNewTrial({ ...newTrial, durationDays: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Redemptions</label>
                  <input
                    type="number"
                    value={newTrial.maxRedemptions}
                    onChange={e => setNewTrial({ ...newTrial, maxRedemptions: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newTrial.description}
                    onChange={e => setNewTrial({ ...newTrial, description: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleCreateTrial}
                  disabled={loading}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create Package
                </button>
                <button
                  onClick={() => setShowCreateTrial(false)}
                  className="px-6 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Trial Packages List */}
          <div className="grid grid-cols-2 gap-4">
            {trialPackages.map(pkg => (
              <div key={pkg.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-900">{pkg.name}</h4>
                    <p className="text-sm text-gray-500">{pkg.description}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    pkg.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {pkg.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Code:</span>
                    <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">{pkg.packageCode}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tier:</span>
                    <TierBadge tier={pkg.tier} />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Duration:</span>
                    <span className="text-gray-900">{pkg.durationDays} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Redemptions:</span>
                    <span className="text-gray-900">
                      {pkg.redemptionCount} / {pkg.maxRedemptions}
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                    <span>Redeem URL:</span>
                    <code className="flex-1 truncate bg-gray-50 px-2 py-1 rounded">
                      vincikit.com/redeem?code={pkg.packageCode}
                    </code>
                    <button
                      onClick={() => copyToClipboard(`vincikit.com/redeem?code=${pkg.packageCode}`)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      title="Copy URL"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenTrialDelivery(pkg)}
                      disabled={!pkg.isActive}
                      className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                    >
                      <Send className="w-4 h-4" />
                      Send Trial Package
                    </button>
                    <button
                      onClick={() => handleDeleteTrialPackage(pkg)}
                      disabled={pkg.redemptionCount > 0}
                      className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      title={pkg.redemptionCount > 0 ? 'Cannot delete - has been redeemed' : 'Delete trial package'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {trialPackages.length === 0 && (
              <div className="col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-500">
                No trial packages yet. Create one to start offering trials.
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Delivery Modal */}
      {showDeliveryModal && deliveryLicense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Package className="w-6 h-6 text-purple-600" />
                  Send Installation Package
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {deliveryLicense.organization} • {deliveryLicense.tier?.toUpperCase()} License
                  {deliveryLicense.status === 'trial' && (
                    <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                      TRIAL
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={() => setShowDeliveryModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Left: Package Options & Email */}
                <div className="space-y-6">
                  {/* Package Options */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Package Contents
                    </h3>
                    <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={deliveryOptions.includeClient}
                          onChange={e => setDeliveryOptions({
                            ...deliveryOptions,
                            includeClient: e.target.checked
                          })}
                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <p className="font-medium text-gray-900">Include Frontend Client</p>
                          <p className="text-xs text-gray-500">Web interface Docker configuration</p>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={deliveryOptions.includeMonitoring}
                          onChange={e => setDeliveryOptions({
                            ...deliveryOptions,
                            includeMonitoring: e.target.checked
                          })}
                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <p className="font-medium text-gray-900">Include Monitoring</p>
                          <p className="text-xs text-gray-500">Prometheus + Grafana setup</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Package Files */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Package Files</h3>
                    <div className="space-y-2">
                      {[
                        { id: 'docker-compose', name: 'docker-compose.yml', desc: 'Docker deployment' },
                        { id: 'env', name: '.env.example', desc: 'Environment config' },
                        { id: 'readme', name: 'README.md', desc: 'Installation guide' },
                        { id: 'init-db', name: 'init-db/01-schema.sql', desc: 'Database setup' }
                      ].map(file => (
                        <button
                          key={file.id}
                          onClick={() => handleLoadPreview(file.id)}
                          className={`w-full text-left px-4 py-2 rounded-lg flex items-center justify-between transition-colors ${
                            deliveryPreviewType === file.id
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                          }`}
                        >
                          <div>
                            <p className="font-medium text-sm">{file.name}</p>
                            <p className="text-xs text-gray-500">{file.desc}</p>
                          </div>
                          <Eye className="w-4 h-4" />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Email Delivery */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email Delivery
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Recipient Email *
                        </label>
                        <input
                          type="email"
                          value={deliveryEmail}
                          onChange={e => setDeliveryEmail(e.target.value)}
                          placeholder="admin@company.com"
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Custom Message (optional)
                        </label>
                        <textarea
                          value={deliveryMessage}
                          onChange={e => setDeliveryMessage(e.target.value)}
                          placeholder="Add a personal note..."
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Preview */}
                <div className="flex flex-col">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Preview: {deliveryPreviewType}
                    </span>
                    <button
                      onClick={() => copyToClipboard(deliveryPreview)}
                      className="text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" />
                      Copy
                    </button>
                  </h3>
                  <div className="flex-1 bg-gray-900 rounded-lg p-4 overflow-auto max-h-[400px]">
                    {deliveryLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
                      </div>
                    ) : (
                      <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap">
                        {deliveryPreview || 'Select a file to preview'}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
              <div className="text-sm text-gray-500">
                License Key: <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">
                  {deliveryLicense.licenseKey?.substring(0, 25)}...
                </code>
                <button
                  onClick={() => copyToClipboard(deliveryLicense.licenseKey)}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  <Copy className="w-3 h-3 inline" />
                </button>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowDeliveryModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGeneratePackage}
                  disabled={deliveryLoading}
                  className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 flex items-center gap-2"
                >
                  {deliveryLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Download ZIP
                </button>
                <button
                  onClick={handleSendDeliveryEmail}
                  disabled={deliveryLoading || !deliveryEmail}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {deliveryLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send Email
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Trial Package Delivery Modal */}
      {showTrialDeliveryModal && deliveryTrialPackage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Gift className="w-6 h-6 text-purple-600" />
                  Send Trial Package
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {deliveryTrialPackage.name} • {deliveryTrialPackage.durationDays} day trial
                  <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                    {LICENSE_TIERS[deliveryTrialPackage.tier]?.name || deliveryTrialPackage.tier}
                  </span>
                </p>
              </div>
              <button
                onClick={() => setShowTrialDeliveryModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              <div className="space-y-6">
                {/* Recipient Info */}
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Important:</strong> This will generate a trial installation package
                    with a unique license key for the recipient. The trial code
                    <code className="mx-1 px-2 py-0.5 bg-blue-100 rounded text-xs">{deliveryTrialPackage.packageCode}</code>
                    will be included in the package.
                  </p>
                </div>

                {/* Organization & Email */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      value={trialDeliveryOrg}
                      onChange={e => setTrialDeliveryOrg(e.target.value)}
                      placeholder="Acme Corporation"
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Recipient Email *
                    </label>
                    <input
                      type="email"
                      value={trialDeliveryEmail}
                      onChange={e => setTrialDeliveryEmail(e.target.value)}
                      placeholder="admin@company.com"
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                {/* Custom Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Message (optional)
                  </label>
                  <textarea
                    value={trialDeliveryMessage}
                    onChange={e => setTrialDeliveryMessage(e.target.value)}
                    placeholder="Add a personal note to the recipient..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Package Contents Summary */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Package Contents
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { name: 'docker-compose.yml', desc: 'Docker deployment configuration' },
                      { name: '.env.example', desc: 'Pre-configured environment file' },
                      { name: 'README.md', desc: 'Installation guide with Docker setup' },
                      { name: 'TRIAL_LIMITATIONS.md', desc: 'Trial terms and upgrade info' },
                      { name: 'init-db/', desc: 'Database initialization scripts' },
                      { name: 'scripts/', desc: 'Backup, restore, and update utilities' }
                    ].map(file => (
                      <div key={file.name} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="font-medium text-sm text-gray-900">{file.name}</p>
                          <p className="text-xs text-gray-500">{file.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Preview Section */}
                {trialDeliveryPreview && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        README Preview
                      </span>
                      <span className="text-xs text-gray-400">
                        (Will use your entered values)
                      </span>
                    </h3>
                    <div className="bg-gray-900 rounded-lg p-4 max-h-[200px] overflow-auto">
                      <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap">
                        {trialDeliveryPreview
                          .replace(/\[ORGANIZATION_NAME\]/g, trialDeliveryOrg || '[ORGANIZATION_NAME]')
                          .replace(/\[EMAIL\]/g, trialDeliveryEmail || '[EMAIL]')
                          .substring(0, 1000)}...
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
              <div className="text-sm text-gray-500">
                Trial Code: <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">
                  {deliveryTrialPackage.packageCode}
                </code>
                <button
                  onClick={() => copyToClipboard(deliveryTrialPackage.packageCode)}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  <Copy className="w-3 h-3 inline" />
                </button>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowTrialDeliveryModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateTrialPackage}
                  disabled={trialDeliveryLoading || !trialDeliveryOrg || !trialDeliveryEmail}
                  className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 flex items-center gap-2"
                >
                  {trialDeliveryLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Download ZIP
                </button>
                <button
                  onClick={handleSendTrialEmail}
                  disabled={trialDeliveryLoading || !trialDeliveryEmail || !trialDeliveryOrg}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {trialDeliveryLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send Email
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
